import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import classnames from 'classnames';
import useRouter from 'use-react-router';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import {
  activeFacebookPageSelector,
  selectActiveFacebookPageFromSession,
} from '@mm/state/selectors/facebookPageSelector';
import {
  activeCompanySelector,
  currentUserRoleSelector,
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { BoxSidebar, BoxSidebarItem, Link, Tooltip } from '@mm/components';
import SwitchCompanyDropdown from '../../common/SwitchCompanyDropdown/SwitchCompanyDropdown';
import { SidebarPage, sidebarPages } from './constants';
import './SettingsSidebar.pcss';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { Routes } from '../../../../routes/top-level-routes';
import { LeftOutlined } from '@ant-design/icons';
import { HelpCenter } from '../../../../common/components/HelpCenter/HelpCenter';
import { useWhitelabelConfig } from '@mm/hooks';
import { AFFILIATES } from '../constants';

type OuterProps = {};

type InnerProps = ReturnType<typeof mapStateToProps> & RouterComponentProps;

type Props = OuterProps & InnerProps;

const useOnClickBack = (props: Props) => {
  const { history, companyId, activeChatbot } = props;

  return useCallback(() => {
    let backLink = Routes.chatbotEditor.getLink();

    if (activeChatbot && activeChatbot.companyId === companyId) {
      backLink = BotBuilderRoutes.dashboard.getLink({
        chatbotId: activeChatbot.id,
      });
    }

    history.push(backLink);
  }, [companyId, activeChatbot, history]);
};

const SettingsSidebar = (props: Props) => {
  const { companies, company } = props;
  const onClickBack = useOnClickBack(props);
  const currentUserRole = useSelector(currentUserRoleSelector);
  const hasAdminPermission = company && company.editable;

  const [isFirstRender, setIsFirstRender] = useState(true);
  const { isWhitelabelCompany, supportEmail = 'support@customers.ai' } =
    useWhitelabelConfig();

  const filterSidebarPages = (sidebarPage: SidebarPage) => {
    const showWithAdminPermission = sidebarPage.requiresAdminPermission
      ? hasAdminPermission
      : true;

    const hideReferral = isWhitelabelCompany && sidebarPage.name !== AFFILIATES;

    return (
      showWithAdminPermission &&
      currentUserRole &&
      !sidebarPage.hiddenToRoles?.includes(currentUserRole) &&
      hideReferral
    );
  };

  const filteredSidebarPages = sidebarPages.filter(filterSidebarPages);

  const facebookPageFromDetails = useSelector(activeFacebookPageSelector);
  const basicFbPageFromSession = useSelector(
    selectActiveFacebookPageFromSession,
  );

  const connectedPageCRUDActions = useConnectedPageCRUDActions();
  const { location } = useRouter();
  const { pathname } = location;
  const currentPage = filteredSidebarPages.find(page => page.path === pathname);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    if (
      basicFbPageFromSession?.id &&
      (!facebookPageFromDetails ||
        facebookPageFromDetails.id !== basicFbPageFromSession?.id)
    ) {
      connectedPageCRUDActions.retrievePage(basicFbPageFromSession.id);
    }
  }, [basicFbPageFromSession?.id]);

  return (
    <BoxSidebar className="settings__sidebar-container">
      <div className="settings-sidebar__back-wrapper" onClick={onClickBack}>
        <LeftOutlined />
        Back
      </div>
      <BoxSidebarItem className="settings__sidebar-item">
        <div className="settings__sidebar-header">
          My account
          <div className="billing-panel__help">
            <Tooltip
              title={
                <div>
                  <span>Contact us at </span>
                  <span>
                    <a
                      href={`mailto:${supportEmail}`}
                      className="settings__sidebar-helplink"
                    >
                      {supportEmail}
                    </a>
                  </span>
                </div>
              }
              isWhite
            >
              <div className="billing-panel__questions-anchor">
                <i className="far fa-question-circle" />
                <span className="billing-panel__questions-anchor--text">
                  Questions?
                </span>
              </div>
            </Tooltip>
            {currentPage?.helpLink && (
              <HelpCenter helpLink={currentPage?.helpLink} />
            )}
          </div>
        </div>
        <SwitchCompanyDropdown
          className="settings-sidebar__company-selector"
          companies={companies}
          showCreateCompany={true}
        />
        <div className="settings-sidebar">
          {filteredSidebarPages.map(sidebarPage => {
            const isActive = pathname.includes(sidebarPage.path);

            return (
              <Link
                key={sidebarPage.id}
                to={sidebarPage.path}
                className={classnames('settings-sidebar__link', {
                  'settings-sidebar__link-active': isActive,
                })}
              >
                <div className="settings-sidebar__link-icon">
                  {sidebarPage.icon}
                </div>
                {sidebarPage.name}
              </Link>
            );
          })}
        </div>
      </BoxSidebarItem>
    </BoxSidebar>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
  companyId: selectActiveCompanyId(state),
  companies: selectCompanies(state),
  activeChatbot: state.chatbot.activeChatbot,
});

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  connect(mapStateToProps),
);

export default enhance(SettingsSidebar);
